import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wind,
  AlertCircle,
  Stethoscope,
  Activity,
  Droplet,
  Waves,
  Skull,
  Shield,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  FileText,
  Users,
  Heart
} from 'lucide-react';

const ProceduresPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeProcedure, setActiveProcedure] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const procedures = [
    {
      id: 1,
      icon: <Wind className="w-12 h-12" />,
      title: "Asthma",
      subtitle: "Chronic Respiratory Condition",
      description: "Comprehensive asthma management including diagnosis, medication management, and lifestyle counseling for better breathing and quality of life.",
      symptoms: ["Wheezing", "Shortness of breath", "Chest tightness", "Coughing"],
      treatments: ["Inhaler therapy", "Nebulization", "Immunotherapy", "Lifestyle management"],
      color: "#2C85B8"
    },
    {
      id: 2,
      icon: <AlertCircle className="w-12 h-12" />,
      title: "Allergies",
      subtitle: "Immune System Response",
      description: "Expert diagnosis and treatment of various allergies including seasonal, food, and environmental allergens through advanced testing and personalized immunotherapy.",
      symptoms: ["Sneezing", "Itchy eyes", "Runny nose", "Skin rashes"],
      treatments: ["Allergy testing", "Immunotherapy", "Medication", "Avoidance strategies"],
      color: "#FF9B6E"
    },
    {
      id: 3,
      icon: <Waves className="w-12 h-12" />,
      title: "Snoring & Sleep Apnea",
      subtitle: "Sleep Disorder Management",
      description: "Advanced diagnosis and treatment of snoring and obstructive sleep apnea through sleep studies, CPAP therapy, and surgical interventions.",
      symptoms: ["Loud snoring", "Daytime fatigue", "Morning headaches", "Poor concentration"],
      treatments: ["Sleep studies", "CPAP therapy", "Positional therapy", "Surgical options"],
      color: "#1B4965"
    },
    {
      id: 4,
      icon: <Activity className="w-12 h-12" />,
      title: "Adenoids",
      subtitle: "Lymphoid Tissue Disorder",
      description: "Diagnosis and treatment of enlarged adenoids causing breathing difficulties, especially in children, through medical and surgical interventions.",
      symptoms: ["Mouth breathing", "Snoring", "Nasal congestion", "Ear infections"],
      treatments: ["Medical management", "Adenoidectomy", "Endoscopic surgery", "Follow-up care"],
      color: "#E86A33"
    },
    {
      id: 5,
      icon: <Activity className="w-12 h-12" />,
      title: "Tonsillitis",
      subtitle: "Throat Infection",
      description: "Expert care for acute and chronic tonsillitis including antibiotic therapy, pain management, and surgical removal when necessary.",
      symptoms: ["Sore throat", "Difficulty swallowing", "Fever", "Swollen neck glands"],
      treatments: ["Antibiotics", "Pain relief", "Tonsillectomy", "Supportive care"],
      color: "#62B6CB"
    },
    {
      id: 6,
      icon: <Stethoscope className="w-12 h-12" />,
      title: "Sinusitis",
      subtitle: "Sinus Inflammation",
      description: "Comprehensive treatment for acute and chronic sinusitis through medical therapy, nasal sprays, and endoscopic sinus surgery when required.",
      symptoms: ["Facial pain", "Nasal congestion", "Headache", "Post-nasal drip"],
      treatments: ["Antibiotics", "Nasal sprays", "Sinus surgery", "Steam therapy"],
      color: "#2C5F7C"
    },
    {
      id: 7,
      icon: <Wind className="w-12 h-12" />,
      title: "COPD",
      subtitle: "Chronic Obstructive Pulmonary Disease",
      description: "Specialized care for COPD including bronchodilators, pulmonary rehabilitation, oxygen therapy, and lifestyle modifications for better lung function.",
      symptoms: ["Chronic cough", "Breathlessness", "Wheezing", "Chest tightness"],
      treatments: ["Bronchodilators", "Inhaled steroids", "Oxygen therapy", "Pulmonary rehabilitation"],
      color: "#E63946"
    },
    {
      id: 8,
      icon: <Activity className="w-12 h-12" />,
      title: "ILD (Interstitial Lung Disease)",
      subtitle: "Lung Tissue Disorders",
      description: "Advanced diagnosis and management of interstitial lung diseases through HRCT, lung biopsy, immunosuppressive therapy, and oxygen support.",
      symptoms: ["Progressive dyspnea", "Dry cough", "Fatigue", "Weight loss"],
      treatments: ["Immunosuppressants", "Oxygen therapy", "Pulmonary rehabilitation", "Lung transplant evaluation"],
      color: "#457B9D"
    },
    {
      id: 9,
      icon: <Heart className="w-12 h-12" />,
      title: "Obesity & Metabolic",
      subtitle: "Weight Management",
      description: "Comprehensive obesity management through dietary counseling, exercise programs, medical therapy, and bariatric surgery referrals when appropriate.",
      symptoms: ["Excessive weight", "Breathing difficulties", "Joint pain", "Fatigue"],
      treatments: ["Diet counseling", "Exercise programs", "Medical therapy", "Behavioral therapy"],
      color: "#FF9B6E"
    },
    {
      id: 10,
      icon: <Skull className="w-12 h-12" />,
      title: "T.B (Tuberculosis)",
      subtitle: "Bacterial Infection",
      description: "Complete TB diagnosis and treatment through DOTS therapy, regular monitoring, and contact tracing to ensure successful cure and prevent spread.",
      symptoms: ["Persistent cough", "Night sweats", "Weight loss", "Fever"],
      treatments: ["DOTS therapy", "Regular monitoring", "Contact tracing", "Preventive therapy"],
      color: "#E86A33"
    },
    {
      id: 11,
      icon: <AlertCircle className="w-12 h-12" />,
      title: "Lung Cancer",
      subtitle: "Malignant Lung Growth",
      description: "Early detection, diagnosis, and comprehensive cancer care through bronchoscopy, biopsy, chemotherapy, radiation therapy, and palliative care.",
      symptoms: ["Persistent cough", "Hemoptysis", "Chest pain", "Weight loss"],
      treatments: ["Chemotherapy", "Radiation therapy", "Surgical resection", "Targeted therapy"],
      color: "#A8DADC"
    },
    {
      id: 12,
      icon: <FileText className="w-12 h-12" />,
      title: "Other Services On Request",
      subtitle: "Customized Medical Care",
      description: "We offer additional specialized services and treatments based on individual patient needs. Contact us to discuss your specific healthcare requirements.",
      symptoms: ["Variable", "Patient-specific", "Consult required"],
      treatments: ["Personalized care", "Specialized referrals", "Multi-disciplinary approach"],
      color: "#1B4965"
    }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', sans-serif;
          overflow-x: hidden;
          color: #1a1a1a;
        }

        .procedures-page {
          width: 100%;
          min-height: 100vh;
          background: linear-gradient(135deg, #F8FAFB 0%, white 100%);
        }

        /* Hero Section */
        .procedures-hero {
          position: relative;
          height: 60vh;
          min-height: 500px;
          background: linear-gradient(135deg, #2C85B8 0%, #1B4965 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .procedures-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -20%;
          width: 70%;
          height: 200%;
          background: radial-gradient(circle, rgba(255, 155, 110, 0.15) 0%, transparent 70%);
          animation: rotate 30s linear infinite;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .procedures-hero-content {
          text-align: center;
          color: white;
          z-index: 2;
          max-width: 900px;
          padding: 2rem;
        }

        .procedures-label {
          font-size: 0.95rem;
          font-weight: 700;
          color: #FFD700;
          letter-spacing: 4px;
          text-transform: uppercase;
          margin-bottom: 1.5rem;
          animation: fadeInDown 0.8s ease;
        }

        .procedures-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: 4.5rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          line-height: 1.1;
          animation: fadeInUp 0.8s ease 0.2s both;
        }

        .procedures-hero-subtitle {
          font-size: 1.3rem;
          opacity: 0.95;
          font-weight: 300;
          line-height: 1.6;
          animation: fadeInUp 0.8s ease 0.4s both;
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Procedures Grid */
        .procedures-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 6rem 2rem;
        }

        .procedures-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: 2.5rem;
        }

        .procedure-card {
          background: white;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          cursor: pointer;
          border: 2px solid transparent;
        }

        .procedure-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 6px;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
        }

        .procedure-card:hover::before {
          transform: scaleX(1);
        }

        .procedure-card:hover {
          transform: translateY(-12px);
          box-shadow: 0 20px 60px rgba(44, 95, 124, 0.2);
          border-color: rgba(232, 106, 51, 0.3);
        }

        .procedure-card-header {
          padding: 2.5rem 2.5rem 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          border-bottom: 1px solid #f0f0f0;
        }

        .procedure-icon-wrapper {
          width: 80px;
          height: 80px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
          transition: all 0.4s ease;
        }

        .procedure-card:hover .procedure-icon-wrapper {
          transform: rotate(-5deg) scale(1.1);
        }

        .procedure-header-content {
          flex: 1;
        }

        .procedure-title {
          font-size: 1.8rem;
          font-weight: 800;
          color: #1B4965;
          margin-bottom: 0.4rem;
          font-family: 'Playfair Display', serif;
        }

        .procedure-subtitle {
          font-size: 0.85rem;
          color: #E86A33;
          font-weight: 600;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .procedure-card-body {
          padding: 2rem 2.5rem 2.5rem;
        }

        .procedure-description {
          font-size: 1rem;
          color: #666;
          line-height: 1.8;
          margin-bottom: 2rem;
        }

        .procedure-section {
          margin-bottom: 1.5rem;
        }

        .procedure-section-title {
          font-size: 0.85rem;
          font-weight: 700;
          color: #1B4965;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .procedure-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.7rem;
        }

        .procedure-list-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.9rem;
          color: #444;
        }

        .list-icon {
          color: #E86A33;
          flex-shrink: 0;
        }

        .procedure-footer {
          padding-top: 1.5rem;
          border-top: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .procedure-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #2C85B8;
          font-weight: 700;
          font-size: 0.95rem;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .procedure-link:hover {
          gap: 1rem;
          color: #E86A33;
        }

        .consult-badge {
          background: linear-gradient(135deg, #E86A33, #FF9B6E);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* CTA Section */
        .procedures-cta {
          background: linear-gradient(135deg, #1B4965 0%, #2C5F7C 100%);
          padding: 6rem 2rem;
          margin: 4rem 0 0;
          position: relative;
          overflow: hidden;
        }

        .procedures-cta::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 70%;
          height: 200%;
          background: radial-gradient(circle, rgba(232, 106, 51, 0.1) 0%, transparent 70%);
        }

        .cta-container {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
          color: white;
          position: relative;
          z-index: 2;
        }

        .cta-title {
          font-family: 'Playfair Display', serif;
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .cta-description {
          font-size: 1.2rem;
          margin-bottom: 3rem;
          opacity: 0.95;
          line-height: 1.7;
        }

        .cta-buttons {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-button {
          padding: 1.2rem 3rem;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.05rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.7rem;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .cta-button-primary {
          background: linear-gradient(135deg, #E86A33, #FF9B6E);
          color: white;
          border: none;
          box-shadow: 0 6px 25px rgba(232, 106, 51, 0.4);
        }

        .cta-button-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 35px rgba(232, 106, 51, 0.5);
        }

        .cta-button-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
        }

        .cta-button-secondary:hover {
          background: white;
          color: #1B4965;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .procedures-hero-title {
            font-size: 2.5rem;
          }

          .procedures-hero-subtitle {
            font-size: 1.1rem;
          }

          .procedures-grid {
            grid-template-columns: 1fr;
          }

          .procedure-list {
            grid-template-columns: 1fr;
          }

          .cta-title {
            font-size: 2rem;
          }

          .cta-description {
            font-size: 1rem;
          }

          .cta-buttons {
            flex-direction: column;
          }

          .cta-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      <div className="procedures-page">
        {/* Hero Section */}
        <section className="procedures-hero">
          <div className="procedures-hero-content">
            <div className="procedures-label">Conditions We Treat</div>
            <h1 className="procedures-hero-title">Expert Care for Every Condition</h1>
            <p className="procedures-hero-subtitle">
              Specialized treatment for respiratory and related conditions with evidence-based medical care and compassionate support
            </p>
          </div>
        </section>

        {/* Procedures Grid */}
        <section className="procedures-container">
          <div className="procedures-grid">
            {procedures.map((procedure, index) => (
              <div 
                key={procedure.id} 
                className="procedure-card"
                onMouseEnter={() => setActiveProcedure(procedure.id)}
                onMouseLeave={() => setActiveProcedure(null)}
                style={{
                  animation: `fadeInUp 0.6s ease ${index * 0.08}s both`
                }}
              >
                <div 
                  className="procedure-card"
                  style={{
                    '--card-color': procedure.color
                  }}
                >
                  <style>{`
                    .procedure-card:hover::before {
                      background: linear-gradient(90deg, ${procedure.color}, ${procedure.color}dd);
                    }
                  `}</style>
                </div>

                <div className="procedure-card-header">
                  <div 
                    className="procedure-icon-wrapper"
                    style={{background: `linear-gradient(135deg, ${procedure.color}, ${procedure.color}dd)`}}
                  >
                    {procedure.icon}
                  </div>
                  <div className="procedure-header-content">
                    <h3 className="procedure-title">{procedure.title}</h3>
                    <p className="procedure-subtitle">{procedure.subtitle}</p>
                  </div>
                </div>

                <div className="procedure-card-body">
                  <p className="procedure-description">{procedure.description}</p>

                  <div className="procedure-section">
                    <div className="procedure-section-title">
                      <AlertCircle size={16} />
                      Common Symptoms
                    </div>
                    <div className="procedure-list">
                      {procedure.symptoms.map((symptom, idx) => (
                        <div key={idx} className="procedure-list-item">
                          <CheckCircle size={16} className="list-icon" />
                          <span>{symptom}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="procedure-section">
                    <div className="procedure-section-title">
                      <Shield size={16} />
                      Treatment Options
                    </div>
                    <div className="procedure-list">
                      {procedure.treatments.map((treatment, idx) => (
                        <div key={idx} className="procedure-list-item">
                          <CheckCircle size={16} className="list-icon" />
                          <span>{treatment}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="procedure-footer">
                    {/* <a href={`/procedures/${procedure.title.toLowerCase().replace(/\s+/g, '-')}`} className="procedure-link"> */}
                    <Link to={`/procedures/${procedure.title.toLowerCase().replace(/\s+/g, '-')}`} className="procedure-link">
  Learn More
  <ArrowRight size={18} />
</Link>
                 
                    <div className="consult-badge">
                      <Users size={16} />
                      Consult Now
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="procedures-cta">
          <div className="cta-container">
            <h2 className="cta-title">Need Expert Medical Consultation?</h2>
            <p className="cta-description">
              Our team of specialized doctors is ready to provide you with comprehensive care and personalized treatment plans for your condition.
            </p>
            <div className="cta-buttons">
              <button className="cta-button cta-button-primary">
                Book Appointment
                <ChevronRight size={20} />
              </button>
              <a href="/contact" className="cta-button cta-button-secondary">
                Contact Us
                <ArrowRight size={20} />
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProceduresPage;